import { useMemo } from 'react';
import { sanitizeHtml } from '@centraoneplus/ui';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  const html = useMemo(() => parseMarkdown(content), [content]);

  return (
    <div
      className={`markdown-content prose prose-sm max-w-none dark:prose-invert ${className}`}
      // H-1: parseMarkdown builds HTML by regex and its escapeHtml does not escape
      // double quotes, so `[x](y" onmouseover="alert(1))` injected an attribute.
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
  );
}

function parseMarkdown(text: string): string {
  let html = escapeHtml(text);

  // Horizontal rule (---)
  html = html.replace(/^---$/gm, '<hr class="my-3 border-gray-300 dark:border-gray-600"/>');

  // Code blocks (``` ... ```)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
    return `<pre class="bg-gray-800 text-gray-100 rounded-lg p-3 my-2 overflow-x-auto text-xs"><code class="language-${lang}">${code.trim()}</code></pre>`;
  });

  // Tables (| Field | Value |)
  html = html.replace(/(\|[^\n]+\|\n)+/g, (tableMatch) => {
    const rows = tableMatch.trim().split('\n');
    let tableHtml = '<table class="w-full text-sm border-collapse my-3 rounded-lg overflow-hidden">';

    rows.forEach((row, index) => {
      // Skip separator row (|---|---|)
      if (row.match(/^\|[\s-:]+\|$/)) return;

      const cells = row.split('|').filter(cell => cell.trim() !== '');
      const isHeader = index === 0;

      tableHtml += '<tr class="' + (isHeader ? 'bg-gray-100 dark:bg-gray-700' : 'bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700') + '">';
      cells.forEach(cell => {
        const tag = isHeader ? 'th' : 'td';
        const cellClass = isHeader
          ? 'px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide'
          : 'px-3 py-2 text-gray-700 dark:text-gray-300';
        // Process bold within cells
        let cellContent = cell.trim().replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>');
        tableHtml += `<${tag} class="${cellClass}">${cellContent}</${tag}>`;
      });
      tableHtml += '</tr>';
    });

    tableHtml += '</table>';
    return tableHtml;
  });

  // Inline code (`code`)
  html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>');

  // Bold (**text** or __text__)
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>');

  // Italic (*text* or _text_)
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

  // Links [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary-600 dark:text-primary-400 hover:underline">$1</a>');

  // Headers (## Header)
  html = html.replace(/^#### (.+)$/gm, '<h5 class="font-semibold text-xs text-gray-800 dark:text-gray-200 mt-3 mb-1">$1</h5>');
  html = html.replace(/^### (.+)$/gm, '<h4 class="font-semibold text-sm text-gray-900 dark:text-white mt-3 mb-1 border-b border-gray-200 dark:border-gray-600 pb-1">$1</h4>');
  html = html.replace(/^## (.+)$/gm, '<h3 class="font-bold text-base text-gray-900 dark:text-white mt-4 mb-2">$1</h3>');
  html = html.replace(/^# (.+)$/gm, '<h2 class="font-bold text-lg text-gray-900 dark:text-white mt-4 mb-2">$1</h2>');

  // Unordered lists
  html = html.replace(/^[\-\*] (.+)$/gm, '<li class="ml-4 list-disc text-gray-700 dark:text-gray-300">$1</li>');
  html = html.replace(/(<li[^>]*>.*<\/li>\n?)+/g, '<ul class="my-2 space-y-1">$&</ul>');

  // Ordered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal text-gray-700 dark:text-gray-300">$1</li>');

  // Line breaks - double newline creates paragraph break
  html = html.replace(/\n\n/g, '</p><p class="mt-2 text-gray-700 dark:text-gray-300">');
  html = html.replace(/\n/g, '<br/>');

  // Wrap in paragraph if not already wrapped
  if (!html.startsWith('<')) {
    html = `<p class="text-gray-700 dark:text-gray-300">${html}</p>`;
  }

  return html;
}

function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
  };
  return text.replace(/[&<>]/g, (m) => map[m] || m);
}

export default MarkdownRenderer;
